import './Home.css';
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import '../../App.css';
import CaughtFishCard from "./components/CaughtFishCard";
import MyAccountCard from "./components/AccountCard";
import LearnMoreCard from "./components/LearnMoreCard";
import NewReport from "../NewReport/NewReport";
import UserBadge from "./components/ProfileCard";
import logo from "../../assets/fishtagged-logo.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faExclamationTriangle, faTriangleExclamation, faCircleExclamation, faAngleRight, faCheckCircle } from "@fortawesome/free-solid-svg-icons";

// Non-intrusive upload notice banner shown after a failed submission redirect
function UploadNoticeBanner({ notice, onDismiss, onViewReports }) {
  if (!notice) return null;

  const isRelogin = notice === 'reloginRequired';
  const isOffline = notice === 'offline';

  const message = isRelogin
    ? "Your session expired — please log in again to retry your upload."
    : isOffline
      ? "You are offline. Connect to the internet and retry the upload."
      : "Something went wrong uploading your report.";

  return (
    <div className="upload-notice-banner" data-type={isRelogin ? 'relogin' : 'error'}>
      <FontAwesomeIcon icon={isRelogin ? faTriangleExclamation : faCircleExclamation} className="upload-notice-icon" />
      <span className="upload-notice-message">{message}</span>
      <button className="upload-notice-action" onClick={onViewReports}>
        View Reports <FontAwesomeIcon icon={faAngleRight} />
      </button>
      <button className="upload-notice-dismiss" onClick={onDismiss} aria-label="Dismiss">
        <FontAwesomeIcon icon={faTimes} />
      </button>
    </div>
  );
}

export default function HomeScreen({ types, user, setUser }) {
  const [showReportPopup, setShowReportPopup] = useState(false);
  const [accountExpanded, setAccountExpanded] = useState(false);
  const [accountError, setAccountError] = useState('');
  const [uploadNotice, setUploadNotice] = useState(null);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.state?.uploadNotice) {
      setUploadNotice(location.state.uploadNotice);
      window.history.replaceState({}, '');
    }
    if (location.state?.showPopup) {
      setShowSuccessPopup(true);
      window.history.replaceState({}, '');
    }
  }, [location.state]);

  useEffect(() => {
    document.body.style.overflow = showReportPopup ? "hidden" : "auto";
  }, [showReportPopup]);

  return (
    <>
      <div className="bg-image" />

      <UploadNoticeBanner
        notice={uploadNotice}
        onDismiss={() => setUploadNotice(null)}
        onViewReports={() => { setUploadNotice(null); navigate('/progress'); }}
      />

      <div className="top-container">
        <div className="top-row">
          <div className="logo">
            <img src={logo} alt="FishTagged Logo" />
          </div>
          <div className="top-badge">
            <UserBadge
              isLoggedIn={user.isLoggedIn}
              name={user.name}
              accountType={user.accountType}
            />
          </div>
        </div>
        <div className={`card-grid${accountExpanded ? ' account-expanded' : ''}`}>
          <CaughtFishCard onOpenReport={() => setShowReportPopup(true)} />
          <MyAccountCard
            isLoggedIn={user.isLoggedIn}
            name={user.name}
            onExpandChange={setAccountExpanded}
            types={types}
            onLoginSuccess={(newUser) => {
              setUser({
                isLoggedIn: true,
                name: newUser.name || newUser.display_name || "",
                accountType: newUser.accountType || newUser.account_type || null,
              });
              setAccountExpanded(false);
            }}
            setAccountError={setAccountError}
            setUser={setUser}
          />
          <LearnMoreCard />
        </div>

        {accountError && (
          <div className="uf-modal-overlay">
            <div className="uf-modal">
              <FontAwesomeIcon icon={faTimes} className="uf-close-icon" onClick={() => setAccountError('')} />
              <FontAwesomeIcon icon={faExclamationTriangle} className="uf-warning-icon" />
              <h2>Account Notice</h2>
              <p>{accountError}</p>
              <div className="uf-modal-buttons">
                <button className="uf-primary-btn" onClick={() => setAccountError('')}>OK</button>
              </div>
            </div>
          </div>
        )}
      </div>

      {showReportPopup && <NewReport onClose={() => setShowReportPopup(false)} />}

      {showSuccessPopup && (
        <div className="uf-modal-overlay">
          <div className="uf-modal">
            <FontAwesomeIcon icon={faCheckCircle} className="uf-success-icon" />
            <h2>Successfully Submitted!</h2>
            <p>Your entry has been uploaded.</p>
            <div className="uf-modal-buttons">
              <button className="uf-primary-btn" onClick={() => setShowSuccessPopup(false)}>OK</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
